import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import NorthIcon from '@mui/icons-material/North';

import { getStudentScoreSummary, getStudentAttemptRate } from 'services/analyticsV2';

const getColorByPercentage = (value, max = 100) => {
  const percentage = max === 5 ? (value / 5) * 100 : value;
  
  if (percentage >= 80) {
    return {
      color: '#009970',
      valueColor: '#00664A'
    };
  } else if (percentage >= 40) {
    return {
      color: '#F5B847',
      valueColor: '#664100'
    };
  } else {
    return {
      color: '#E2483C',
      valueColor: '#C51407'
    };
  }
};

const isCodingVariant = (type) => type === 'coding_mock' || type === 'coding_iw';
const isVerbalVariant = (type) => type === 'verbal_mock' || type === 'verbal_iw' || type === 'assessment';

const computeAttemptRateValue = (attemptData) => {
  if (attemptData && attemptData.totalTemplates) {
    return Math.round((attemptData.totalAttempted / attemptData.totalTemplates) * 100);
  }
  return 0;
};

const computeVerbalScores = (summary) => {
  const overall = Math.round((summary.communicationOverall + summary.contentOverall) / 2);
  const speech = Math.round(summary.communicationOverall);
  const content = Math.round(summary.contentOverall);
  const highest = summary.highestAverage ?? Math.max(speech, content);
  const lowest = summary.lowestAverage ?? Math.min(speech, content);
  return { overall, speech, content, highest, lowest };
};

const buildFourCData = (summary, coding, coreConcepts, criticalThinking, content) => ([
  {
    key: 'coding',
    label: 'Coding',
    value: coding,
    max: 5,
    change: null,
    changeType: null,
    color: '#C51407',
    border: true,
    percentageChange: summary.coding?.percentageChange,
    details: [
      { label: 'DSA Usage & Understanding', value: Math.round(summary.coding?.DSA_USAGE_AND_UNDERSTANDING ?? 0) },
      { label: 'Edge Case Handling', value: Math.round(summary.coding?.EDGE_CASE_HANDLING ?? 0) },
      { label: 'Code Organization & Clarity', value: Math.round(summary.coding?.CODE_ORGANIZATION_AND_CLARITY ?? 0) },
      { label: 'Scalability & Efficiency', value: Math.round(summary.coding?.SCALABILITY_AND_EFFICIENCY ?? 0) },
    ],
  },
  {
    key: 'core',
    label: 'Core Concepts',
    value: coreConcepts,
    max: 5,
    change: null,
    changeType: null,
    color: '#664100',
    border: false,
    percentageChange: summary.coreConcepts?.percentageChange,
    details: [
      { label: 'Conceptual Clarity', value: Math.round(summary.coreConcepts?.CONCEPTUAL_CLARITY ?? 0) },
    ],
  },
  {
    key: 'critical',
    label: 'Critical Thinking',
    value: criticalThinking,
    max: 5,
    change: null,
    changeType: null,
    color: '#00664A',
    border: false,
    percentageChange: summary.criticalThinking?.percentageChange,
    details: [
      { label: 'Logical Reasoning & Analysis', value: Math.round(summary.criticalThinking?.LOGICAL_REASONING_AND_ANALYSIS ?? 0) },
      { label: 'Adaptive Improvement', value: Math.round(summary.criticalThinking?.ADAPTIVE_IMPROVEMENT ?? 0) },
    ],
  },
  {
    key: 'comm',
    label: 'Communication',
    value: content,
    max: 5,
    change: null,
    changeType: null,
    color: '#00664A',
    border: false,
    percentageChange: summary.content?.percentageChange,
    details: [
      { label: 'Explanation & Logical Coherence', value: Math.round(summary.content?.EXPLANATION_AND_LOGICAL_COHERENCE ?? 0) },
    ],
  },
]);

function CircularProgress({ value, max = 100, size = 180, stroke = 16, label, sublabel }) {
  const { color, valueColor } = getColorByPercentage(value, max);
  const percentage = max === 5 ? (value / 5) * 100 : value;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#F3F3F3"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s' }}
        />
        <text
          x="50%"
          y="48%"
          textAnchor="middle"
          fontSize={33}
          fontWeight="600"
          fill={valueColor}
          dominantBaseline="middle"
          style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}
        >
          {Math.round(value)}
        </text>
        <text
          x="50%"
          y="62%"
          textAnchor="middle"
          fontSize={19}
          fontWeight="500"
          fill="#4E5661"
          dominantBaseline="middle"
          style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}
        >
          {label}
        </text>
      </svg>
      {sublabel && (
        <Typography fontSize={16} color="#999" mt={-0.5}>
          {sublabel}
        </Typography>
      )}
    </Box>
  );
}

function BarProgress({ value, label, marker, max = 100 }) {
  const { color, valueColor } = getColorByPercentage(value, max);
  const percentage = max === 5 ? (value / 5) * 100 : value;
  
  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6-medium" color="#4E5661">
          {label}
        </Typography>
        <Typography variant="h6-bold" color={valueColor}>
          {value}
        </Typography>
      </Box>
      <Box position="relative" width="100%" height={14}>
        <Box position="absolute" left={0} top={0} width="100%" height={14} bgcolor="#E9F0F7" borderRadius={1} border="1px solid #CCD4DE" />
        <Box position="absolute" left={0} top={0} width={`${percentage}%`} height={14} bgcolor={color} borderRadius={7} />
        {marker !== undefined && (
          <>
            <Box
              position="absolute"
              left={`${marker}%`}
              top={-8}
              width={0}
              height={30}
              borderLeft="2px dashed #6B7683"
              zIndex={2}
              sx={{ transform: 'translateX(-1px)' }}
            />
            <svg
              width="18"
              height="10"
              viewBox="0 0 18 10"
              style={{ 
                position: 'absolute', 
                left: `${marker}%`, 
                top: 14, 
                transform: 'translateX(-9px) rotate(180deg)' 
              }}
            >
              <polygon points="0,0 18,0 9,10" fill="#6B7683" />
            </svg>
          </>
        )}
      </Box>
    </Box>
  );
}

function FourCBox({ label, value, max, onClick, percentageChange }) {
  const { valueColor } = getColorByPercentage(value, max);
  const [hover, setHover] = useState(false);
  
  const isPositive = percentageChange >= 0;
  const changeColor = isPositive ? "#246E57" : "#C51407";
  const changeBgColor = "#fff";
  const changeBorderColor = isPositive ? "#246E57" : "#C51407";
  const displayValue = percentageChange !== undefined ? `${Math.abs(percentageChange)}%` : "20%";
  
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      sx={{
        border: hover ? '1px solid #003E8C' : '1px solid #EDF1F7',
        borderRadius: '8px',
        backgroundColor: '#EDF1F7',
        cursor: 'pointer',
        minHeight: 64,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography
          variant="h6-medium"
        >
          {label}
        </Typography>
        <svg width="22" height="22" style={{ marginLeft: 8 }}>
          <polyline
            points="8,6 14,11 8,16"
            fill="none"
            stroke='#1976D2'
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
      <Box display="flex" alignItems="center"  mt={0.5} gap={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5-semiBold" color={valueColor} mr={0.5}>
            {Math.round(value)}
          </Typography>
          <Typography variant="h5-semiBold" color={valueColor}>
            /{max}
          </Typography>
        </Box>
          <Box 
           display="flex" 
           alignItems="center" 
           justifyContent="center" 
           bgcolor={changeBgColor} 
           border={`1px solid ${changeBorderColor}`} 
           borderRadius='4px' 
           px={1}
           gap={0.5}
         >
           <NorthIcon 
             sx={{ 
               fontSize: 14, 
               color: changeColor,
               transform: isPositive ? 'rotate(0deg)' : 'rotate(180deg)',
               transition: 'transform 0.2s ease'
             }} 
           />
           <Typography variant="body01-bold" color={changeColor}>
             {displayValue}
           </Typography>
         </Box>
      </Box>
    </Box>
  );
}


function VerticalAttemptBar({ value = 0 }) {
  const percent = Math.max(0, Math.min(100, value));
  const { color, valueColor } = getColorByPercentage(percent, 100);
  
  return (
    <Box
      border="1px solid #8692A3"
      borderRadius={2}
      flex={1}
      p={2}
      ml={2}
      bgcolor="#fff"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      height="100%"
      position="relative"
      maxWidth='96px'
    >
      <Typography variant="h6-medium" mb={1} sx={{textAlign:'center'}}>
        Attempt Rate
      </Typography>
      <Box
        position="relative"
        width={52}
        flex={1}
        bgcolor="#F2F6FB"
        borderRadius={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-end"
        mt={1}
        mb={1}
        minHeight={100}
      >
        <Typography
          variant="body02-medium"
          color="#6B7683"
          style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }}
        >
          100
        </Typography>
        <Box
          position="absolute"
          left={0}
          bottom={0}
          width={52}
          height={`calc(${percent}% - 2px)`}
          bgcolor={color}
          borderRadius={2}
          style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </Box>
      <Box width='100%' display="flex" justifyContent="center" alignItems="center" mt={0.5}>
        <Typography variant="body02-medium" color="#6B7683" style={{ fontSize: 13 }}>
          Attempted
        </Typography>
      </Box>
      <Typography variant="h6-bold" color={valueColor} mt={0.5}>
        {percent}%
      </Typography>
    </Box>
  );
}

function ScoreSummarySkeleton({ interviewVariantType, activeTab }) {
  const isCoding = interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw';
  
  return (
    <Box p={2} width="100%">
      <Box display="flex" flexDirection="column" mb={8} gap={2}>
      <Typography variant="h4-medium" >
                Student score summary
            </Typography>
            <Typography variant="h6-medium" color="neutral.clr-600">
                Assess student performance through scoring insights
            </Typography>
        <Divider sx={{borderColor: '#CCD4DE'}}/>
      </Box>
      
      <Box display="flex" gap={2} width="100%" alignItems="stretch">
        <Box 
          flex={1} 
          minWidth={isCoding ? 220 : 260} 
          width='100%' 
          p={5} 
          border="1px solid #8692A3" 
          borderRadius={2} 
          display="flex" 
          alignItems="center" 
          bgcolor="#fff"
        >
          {isCoding ? (
            <Box display="flex" alignItems="center" gap={3} width="100%">
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minWidth={120}>
                <Skeleton variant="circular" width={180} height={180} />
              </Box>
              
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} flex={1}>
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton key={item} variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            </Box>
          ) : (
              <Box display="flex" alignItems="center" gap={5} width="100%">
              <Box flexShrink={0}>
                <Skeleton variant="circular" width={180} height={180} />
              </Box>
              
              <Divider orientation="vertical" flexItem />
              
              <Box display="flex" flexDirection="column" gap={3} ml={2} width="100%">
                {[1, 2].map((item) => (
                  <Box key={item} width="100%">
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Skeleton variant="text" width={120} height={24} />
                      <Skeleton variant="text" width={40} height={24} />
                    </Box>
                    <Skeleton variant="rectangular" width="100%" height={14} sx={{ borderRadius: 1 }} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
        
        <Box 
          display="flex" 
          flexDirection="row" 
          gap={6} 
          p={5} 
          border="1px solid #8692A3" 
          borderRadius={2} 
          minWidth={isCoding ? 424 : 120} 
          alignItems="center" 
          bgcolor="#fff"
        >
          <Skeleton variant="circular" width={180} height={180} />
          <Skeleton variant="circular" width={180} height={180} />
        </Box>
        
        {activeTab !== 'interviewWizard' && (
          <Box
            border="1px solid #8692A3"
            borderRadius={2}
            flex={1}
            p={2}
            ml={2}
            bgcolor="#fff"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            height="100%"
            position="relative"
            maxWidth='96px'
          >
            <Skeleton variant="text" width={80} height={24} sx={{ mb: 1 }} />
            
            <Box
              position="relative"
              width={52}
              flex={1}
              mt={1}
              mb={1}
              minHeight={100}
            >
              <Skeleton variant="rectangular" width={52} height={100} sx={{ borderRadius: 1 }} />
            </Box>
            
            <Skeleton variant="text" width={60} height={16} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width={40} height={24} sx={{ mt: 0.5 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function FourCExpandedView({ selectedData, summary, onBack }) {
  const { valueColor } = getColorByPercentage(selectedData.value, 5);
  
  return (
    <Box display="flex" flexDirection="column" flex={1} p={3} bgcolor="#EDF1F7" borderRadius={2} maxHeight='180px' overflow='auto'>
      <Box display="flex" alignItems="center" mb={3}>
        <Box 
          display="flex" 
          alignItems="center" 
          sx={{ cursor: 'pointer' }}
          onClick={onBack}
        >
          <svg width="20" height="20" style={{ marginRight: 8 }}>
            <polyline 
              points="12,5 7,10 12,15" 
              fill="none" 
              stroke="#1976D2" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <Typography variant="h6-medium" color="#1976D2">
            {selectedData.label}
          </Typography>
        </Box>
      </Box>
      
      <Box display="flex" flexDirection="column" gap={2}>
        {selectedData.details.map((detail, index) => (
          <Box 
            key={index}
            display="flex" 
            alignItems="center" 
            justifyContent="space-between" 
            bgcolor="#fff" 
            borderRadius={1} 
            p={2}
            border="1px solid #E0E0E0"
          >
            <Typography variant="h6-medium" color="#23272E">
              {detail.label}
            </Typography>
            <Typography variant="h6-bold" color={valueColor}>
              {detail.value}/5
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function ScoreSummary({ studentId, interviewVariantType, batchId, activeTab }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedC, setSelectedC] = useState(null);
  const [attemptRate, setAttemptRate] = useState(null);

  useEffect(() => {
    async function fetchSummary() {
      setLoading(true);
      setError(null);
      try {
        const summaryData = activeTab === 'interviewWizard'
          ? await getStudentScoreSummary(studentId, interviewVariantType)
          : await getStudentScoreSummary(studentId, interviewVariantType, batchId);
        setSummary(summaryData);

        const attemptData = activeTab === 'interviewWizard'
          ? await getStudentAttemptRate(studentId, interviewVariantType)
          : await getStudentAttemptRate(studentId, interviewVariantType, batchId);
        setAttemptRate(computeAttemptRateValue(attemptData));
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (studentId && interviewVariantType && (batchId || activeTab === 'interviewWizard')) fetchSummary();
  }, [studentId, interviewVariantType, batchId, activeTab]);

  if (loading) return <ScoreSummarySkeleton interviewVariantType={interviewVariantType} activeTab={activeTab} />;
  if (error) return <Box p={4}><Typography color="error">{error}</Typography></Box>;
  if (!summary) return null;

  if (isVerbalVariant(interviewVariantType)) {
    const { overall, speech, content, highest, lowest } = computeVerbalScores(summary);
    return (
      <Box p={2} width="100%">
        <Box display="flex" flexDirection="column" mb={8} gap={2} >
            <Typography variant="h4-medium" >
                Student score summary
            </Typography>
            <Typography variant="h6-medium" color="neutral.clr-600">
                Assess student performance through scoring insights
            </Typography>
            <Divider sx={{borderColor: '#CCD4DE'}}/>
        </Box>
        <Box display="flex" gap={2} width="100%" alignItems="stretch">
          <Box flex={1} minWidth={260} width='100%' p={5} border="1px solid #8692A3" borderRadius={2} display="flex" alignItems="center" bgcolor="#fff">
            <Box display="flex" alignItems="center" gap={5} width="100%">
              <Box flexShrink={0}>
                <CircularProgress value={overall} color="#F5B847" valueColor="#664100" label="Overall score" />
              </Box>
              <Divider orientation="vertical" flexItem sx={{borderColor: '#CCD4DE'}}/>
              <Box display="flex" flexDirection="column" gap={3} ml={2} width="100%">
                <BarProgress value={speech} color="#F5B847" valueColor="#664100" label="Speech score" marker={speech} />
                <BarProgress value={content} color="#009970" valueColor="#00664A" label="Content score" marker={content} />
              </Box>
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" gap={6} p={5} border="1px solid #8692A3" borderRadius={2} minWidth={120} alignItems="center" bgcolor="#fff">
            <CircularProgress value={highest} color="#009970" valueColor="#00664A" label="Highest score" />
            <CircularProgress value={lowest} color="#E2483C" valueColor="#C51407" label="Lowest score" />
          </Box>
          {activeTab !== 'interviewWizard' && <VerticalAttemptBar value={attemptRate} />}
        </Box>
      </Box>
    );
  }

  if (isCodingVariant(interviewVariantType)) {
    const coding = summary.coding?.overall ?? 0;
    const coreConcepts = summary.coreConcepts?.overall ?? 0;
    const criticalThinking = summary.criticalThinking?.overall ?? 0;
    const content = summary.content?.overall ?? 0;
    const avg4C = Math.round((coding + coreConcepts + criticalThinking + content) / 4);
    const fourCData = buildFourCData(summary, coding, coreConcepts, criticalThinking, content);
    const highest = summary.highestScore ?? Math.max(coding, coreConcepts, criticalThinking, content);
    const lowest = summary.lowestScore ?? Math.min(coding, coreConcepts, criticalThinking, content);
    return (
      <Box p={2}>
        <Box display="flex" flexDirection="column" mb={8} gap={2} >
            <Typography variant="h4-medium" >
                Student score summary
            </Typography>
            <Typography variant="h6-medium" color="neutral.clr-600">
                Assess student performance through scoring insights
            </Typography>
            <Divider sx={{borderColor: '#CCD4DE'}}/>
        </Box>
        <Box display="flex" gap={2} width="100%" alignItems="stretch">
          <Box p={5} border="1px solid #8692A3" borderRadius={2} display="flex" alignItems="flex-start" gap={3} bgcolor="#fff" minWidth={220} width='100%'>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minWidth={120}>
              <CircularProgress value={avg4C} max={5} color="#F5B847" valueColor="#664100" label='Overall score' />
            </Box>
            <Divider orientation="vertical" flexItem sx={{borderColor: '#CCD4DE'}}/>
            {selectedC ? (
              <FourCExpandedView 
                selectedData={fourCData.find(c => c.key === selectedC)}
                summary={summary}
                onBack={() => setSelectedC(null)}
              />
            ) : (
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} flex={1}>
                {fourCData.map((c, idx) => (
                  <FourCBox
                    key={c.key}
                    {...c}
                    onClick={() => setSelectedC(c.key)}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Box display="flex" gap={6} p={5} border="1px solid #8692A3" borderRadius={2} minWidth={424}  alignItems="center">
            <CircularProgress value={highest} max={5} color="#009970" valueColor="#00664A" label="Highest score" />
            <CircularProgress value={lowest} max={5} color="#E2483C" valueColor="#C51407" label="Lowest score" />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
}
